
// @ts-nocheck - disable typescript due to to many type errors

// Packages
import debounce from 'lodash/debounce';
import { mapActions, mapState } from 'pinia';
import { defineComponent, PropType } from 'vue';

// Helpers
import { datadogRum } from '@datadog/browser-rum';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import {
  DATE_TIME_FORMATS,
  format,
  utcToZonedTime,
} from '@white-label-helper/date-utilities';
import { trackUserIdentity } from '@white-label-helper/ga-tracking';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { smoothScroll } from '@white-label-helper/scroll';
import { addCustomMetric } from '@white-label-helper/shared-helpers';
import {
  DOMAINS_NAMES,
  getDomainUrl,
  goToApp,
  MANAGE_BOOKING_URLS,
} from '@white-label-helper/switch-app';
import {
  getFeatureFlag,
  isGtm2025LoungePassAndTravelInsuranceCombinedEnabled,
  trackLaunchDarklyExperimentImpression,
} from '@white-label-plugin/launch-darkly';
import {
  email,
  maxLength,
  minLength,
  notNumber,
  postalAreaCode,
  required,
  requiredIf,
  sameAs,
  name,
} from '@white-label-helper/vuelidate';
import { isSycurioFlow } from '@white-label-helper/is-sycurio-flow';

// Constants
import {
  CANT_FIND_FLIGHT_ID,
  FEATURE_FLAGS,
  PAYMENT_PROVIDERS,
  PRODUCT_TYPES,
  TEXT_INPUT_DEBOUNCE_TIME,
  VEHICLE_OCCUPANTS,
} from '@white-label-configuration/constants';

// Mixins
import checkoutForm from '@white-label-helper/mixin-checkout-form';

// Store
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import { useDiscountCodeStore } from '@white-label-store/discount-code';
import { useMarketingPreferencesStore } from '@white-label-store/marketing-preferences';
import { usePaymentFormStore } from '@white-label-store/payment-form';
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';
import { useTravelInsuranceStore } from '@white-label-store/travel-insurance';
import { useLoungePassStore } from '@white-label-store/lounge-pass';
import { useUserInfoStore } from '@white-label-store/user-info';

// Components
import Button from '../button/button.vue';
import CancellationAndAmendmentPolicy from '../cancellation-and-amendment-policy/cancellation-and-amendment-policy.vue';
import CancellationAndAmendmentPolicyNew from '../cancellation-and-amendment-policy-new/cancellation-and-amendment-policy.vue';
import Checkbox from '../checkbox/checkbox.vue';
import DiscountCode from '../discount-code/discount-code.vue';
import DropdownWrapper from '../dropdown-wrapper/dropdown-wrapper.vue';
import FlightLookup from '../flight-lookup/flight-lookup.vue';
import IconMessage from '../icon-message/icon-message.vue';
import LookupConfirmation from '../lookup-confirmation/lookup-confirmation.vue';
import PaymentGatewayAdvam from '../payment-gateway-advam/payment-gateway-advam.vue';
import PaymentGatewayBraintree from '../payment-gateway-braintree/payment-gateway-braintree.vue';
import PaymentGatewayStripe from '../payment-gateway-stripe/payment-gateway-stripe.vue';
import PaymentGatewayWindcave from '../payment-gateway-windcave/payment-gateway-windcave.vue';
import PaymentGatewaySycurio from '../payment-gateway-sycurio/payment-gateway-sycurio.vue';
import PaymentMarketingChannels from '../payment-marketing-channels/payment-marketing-channels.vue';
import PaymentPageAdditionalTravellers from '../payment-page-additional-travellers/payment-page-additional-travellers.vue';
import type { AdditionalTravellerProduct } from '../payment-page-additional-travellers-products-list/payment-page-additional-travellers-products-list.vue';
import PhoneField from '../phone-field/phone-field.vue';
import TextField from '../text-field/text-field.vue';
import Tooltip from '../tooltip/tooltip.vue';

import {
  AdditionalTravellersCombinedProductsList,
  AdditionalTravellersCombinedProductsModal,
  AdditionalTravellersCombinedProductForm,
  AdditionalTravellersCombinedProductSummary,
} from 'ecommerce/feature-additional-travellers-combined-products';

// Types
import type {
  AmendmentPolicy,
  CancellationPolicy,
} from '@white-label-types/parking-booking';
import type {
  AdvamConfig,
  BraintreeConfig,
  CancellationProtection,
  CartItem,
  ParsedDiscount,
  StripeConfig,
  Total,
  WindcaveConfig,
} from '@white-label-types/parking-checkout';
import type { MetaData, UserData } from '@white-label-types/stores';
import type { Terminal } from '@white-label-types/search-box';
import type VueI18n from 'vue-i18n';
import type { CustomerGroupsConfig } from '@white-label-types/product-types';
import {
  type CustomerGroupLabels,
  getProductTypeCustomerGroupLabels,
} from '@white-label-helper/get-product-type-customer-group-labels';
import IconS3 from '@white-label-icon/icons-s3';

export default defineComponent({
  components: {
    ButtonControl: Button,
    Checkbox: Checkbox,
    PaymentGatewaysSharedBraintree: PaymentGatewayBraintree,
    PaymentGatewaysSharedStripe: PaymentGatewayStripe,
    PaymentGatewaysSharedWindcave: PaymentGatewayWindcave,
    PaymentGatewaysSharedAdvam: PaymentGatewayAdvam,
    PaymentGatewaysSharedSycurio: PaymentGatewaySycurio,
    PaymentPageAdditionalTravellers,
    IconS3,
    IconLockStar: () => import('@white-label-icon/icon-lock-star'),
    IconLockLocked: () => import('@white-label-icon/icon-lock-locked'),
    IconMessage,
    DiscountCode: DiscountCode,
    TextField,
    PhoneField,
    MarketingChannels: PaymentMarketingChannels,
    CancellationAndAmendmentPolicy,
    CancellationAndAmendmentPolicyNew,
    DropdownWrapper,
    FlightLookup,
    LookupConfirmation,
    Tooltip,
    AdditionalTravellersCombinedProductsList,
    AdditionalTravellersCombinedProductsModal,
    AdditionalTravellersCombinedProductForm,
    AdditionalTravellersCombinedProductSummary,
  },

  mixins: [checkoutForm],

  props: {
    title: {
      type: String,
      default: '',
    },
    cancellationPolicies: {
      type: Array as PropType<
        | CancellationPolicy[]
        | CartItem['inventory_option']['cancellation_policies'][]
      >,
      default: () => [],
    },
    amendmentPolicies: {
      type: Array as PropType<
        AmendmentPolicy[] | CartItem['inventory_option']['amendment_policies'][]
      >,
      default: () => [],
    },
    paymentsConfig: {
      type: Object as PropType<
        BraintreeConfig | StripeConfig | WindcaveConfig | AdvamConfig
      >,
      required: true,
      default: () => ({
        token: '',
        apiKey: '',
      }),
    },
    paymentsConfigReceived: {
      type: Boolean,
      required: true,
      default: false,
    },
    paymentIntentUpdated: {
      type: Boolean,
      default: false,
    },
    orderTotal: {
      type: Number,
      default: 0,
    },
    cartToken: {
      type: String,
      default: '',
    },
    isProtected: {
      type: Boolean,
      required: true,
    },
    cancellationProtection: {
      type: Array as PropType<CancellationProtection[]>,
      required: true,
    },
    isBookingFeeCharged: {
      type: Boolean,
      required: true,
      default: false,
    },
    cartItems: {
      type: Array as PropType<CartItem[]>,
      default: () => [] as CartItem[],
    },
    extendFlow: {
      type: Boolean,
      required: false,
      default: false,
    },
    orderTotals: {
      type: Object as PropType<Total>,
      default: () => ({}),
    },
  },

  validations() {
    // null is allowed here to support the "can't-find-flight" option.
    // Empty string should still fail validation.
    const conditionallyRequired = (fieldName) => {
      return function (value) {
        const required = this.checkoutForm_checkIfFieldRequired(fieldName);
        if (!required) return true;
        if (value === null) return true;
        return typeof value === 'string' && value.trim() !== '';
      };
    };
    const userDetailsValidations = {
      first_name: {
        required,
        notNumber,
        name,
      },
      last_name: {
        required,
        notNumber,
        name,
      },
      license_plate: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('plate_number');
        }),
      },

      number_of_people_in_vehicle: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'number_of_people_in_vehicle',
          );
        }),
      },
      number_of_child_seats: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'is_child_seat_required',
          );
        }),
      },
      cruise_ship_name: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('cruise_ship_name');
        }),
        maxLength: maxLength(this.cruiseShipNameMaxLength),
      },
      terminal_name: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('terminal_name');
        }),
      },
      email: {
        required,
        email,
      },
      phone: {
        phone_number: {
          required: requiredIf(() => {
            // @ts-ignore - `this` is not recognized in its context
            if (!this.checkoutForm_checkFieldVisibility('phone_number'))
              return false;
            // @ts-ignore - `this` is not recognized in its context
            return (
              this.checkoutForm_checkIfFieldRequired('phone_number') ||
              this.userDetails.marketing_channels?.sms
            );
          }),

          isValid: (): boolean => {
            const isEmpty = !this.userDetails.phone?.phone_number?.length;
            return !isEmpty ? this.phoneValid : true;
          },
        },
      },
      zipcode: this.isPostalAreaCodeMandatoryField
        ? {
            required,
            minLength: minLength(2),
            postalAreaCode,
          }
        : {
            required: requiredIf(function checkIfRequired() {
              // @ts-ignore - `this` is not recognized in its context
              return this.checkoutForm_checkIfFieldRequired('zipcode');
            }),
          },
      outbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return (
            this.checkoutForm_checkIfFieldRequired('outbound_flight_number') &&
            !this.displayFlightLookup
          );
        }),
      },
      inbound_flight: {
        required: conditionallyRequired('inbound_flight_number'),
      },
      ...(this.isBookingPortalFlowEnabled
        ? {
            others_email: {
              email,
            },
            booker_email: {
              // @ts-ignore - `this` is not recognized in its context
              required: requiredIf(() => this.isBookingPortalFlowEnabled),
              email,
            },
          }
        : {}),
    };

    if (!this.displayVehicleLookUp || this.vehicleLookUp.enterManually) {
      userDetailsValidations.vehicle_make = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_make');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      };
      userDetailsValidations.vehicle_model = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_model');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      };
      userDetailsValidations.vehicle_colour = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_colour');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      };
    }

    if (this.hasTravelInsuranceInCart) {
      userDetailsValidations.town_or_city = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('full_address');
        }),
      };
      userDetailsValidations.address_line_1 = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('full_address');
        }),
      };
    }

    const travellerNamesValidation = {
      required,
      notNumber,
      name,
    };
    const validations = {
      userDetails: {
        ...userDetailsValidations,
      },
      additionalTravellersDetails: {
        $each: {
          firstName: travellerNamesValidation,
          lastName: travellerNamesValidation,
        },
      },
      emailConfirmation: {
        noPaste(): boolean {
          // @ts-ignore - `this` is not recognized in its context
          return !this.attemptToPaste;
        },
        sameAs: sameAs(function sameAsEmail() {
          // @ts-ignore - `this` is not recognized in its context
          return this.userDetails.email;
        }),
        required,
      },
      vehicleLookUp: {
        vehicleMaxWeightConfirmed: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore
            return this.checkoutForm_checkIfFieldRequired('max_vehicle_weight');
          }),
          isConfirmed(value: boolean) {
            if (!this.checkoutForm_checkIfFieldRequired('max_vehicle_weight'))
              return true;
            return value === true;
          },
        },
        vehicleRoadworthyConfirmed: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore
            return this.checkoutForm_checkIfFieldRequired('vehicle_roadworthy');
          }),
          isConfirmed(value: boolean) {
            if (!this.checkoutForm_checkIfFieldRequired('vehicle_roadworthy'))
              return true;
            return value === true;
          },
        },
      },
    };

    if (this.displayLoungePassAdditionalTravellers) {
      validations.loungePassAdditionalTravellersDetails = {
        $each: {
          firstName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return (
                this.isLoungePassAdditionalTravellersDetailsRequired ||
                traveller.lastName.length
              );
            }),
            notNumber,
            name,
          },
          lastName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return (
                this.isLoungePassAdditionalTravellersDetailsRequired ||
                traveller.firstName.length
              );
            }),
            notNumber,
            name,
          },
        },
      };
    }

    return validations;
  },

  data() {
    const defaultData = {
      disableButton: false,
      userDetails: {
        first_name: '',
        last_name: '',
        email: '',
        phone: {
          phone_number: '',
          formatted_number: '',
          country_code: '',
          country_iso_code: '',
        },
        license_plate: '',
        number_of_people_in_vehicle: null,
        number_of_child_seats: null,
        zipcode: '',
        outbound_flight: '',
        outbound_airline: '',
        outbound_airport: '',
        outbound_terminal: '',
        inbound_flight: '',
        inbound_airline: '',
        inbound_airport: '',
        inbound_terminal: '',
        marketing_channels: {},
        booker_email: '',
        others_email: '',
        send_booker_email: false,
        send_customer_email: false,
        send_others_email: false,
        vehicle_make: '',
        vehicle_model: '',
        vehicle_colour: '',
        cruise_ship_name: '',
        terminal_name: '',
        town_or_city: '',
        address_line_1: '',
        address_line_2: '',
        is_customer_email_confirmed: false,
      } as UserData,
      metaData: {
        cost_centre: '',
      } as MetaData,
      emailConfirmation: '',
      attemptToPaste: false,
      phoneValid: false,
      showDiscountCode: false,
      validatingDiscount: false,
      discountError: '',
      onEmailChangeDebounced: null as (() => void) | null,
      showMarketingPreferences: false,
      booker_email: '',
      others_email: '',
      isNewUser: false,
      showDropdownExpanded: false,
      numberInVehicleOpened: false,
      numberChildSeatsOpened: false,
      terminalNameOpened: false,
      vehicleLookUp: {
        error: false,
        loading: false,
        success: false,
        enterManually: false,
        showDetails: false,
        vehicleMaxWeightConfirmed: false,
        vehicleRoadworthyConfirmed: false,
        grossVehicleWeight: null,
        vehicleRoadworthyValid: false,
        vehicleRoadworthinessAndWeightError: '',
      },
      flightLookupTooltipVisible: false,
      isPaymentPageAdditionalTravellersProductsListModalVisible: false,
    };
    return {
      ...defaultData,
      additionalTravellersDetails: [],
      // Data to store Priority travellers details

      loungePassAdditionalTravellersDetails: [],
    };
  },

  computed: {
    ...mapState(useUserInfoStore, {
      getAllUserDetails: 'readUserDetails',
      inboundFlightLookupItems: 'readInboundFlightLookupItems',
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      inboundFlightSelected: 'readInboundFlightLookupSelected',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
      findOutboundFlightById: 'findOutboundFlightById',
      flightLookupLoading: 'readFlightLookupLoading',
    }),

    ...mapState(useMarketingPreferencesStore, {
      readMarketingPreferences: 'readMarketingPreferences',
      readUserMarketingSubscription: 'readUserMarketingSubscription',
      readMarketingStrategy: 'readMarketingStrategy',
      readCustomerUUID: 'readCustomerUUID',
    }),

    ...mapState(useCartCheckoutStore, {
      readCartItemsType: 'readCartItemsType',
      readCartItems: 'readCartItems',
    }),

    ...mapState(usePaymentFormStore, {
      readPaymentFormData: 'readPaymentFormData',
      readVehicleLookUpState: 'readVehicleLookUpState',
    }),

    ...mapState(useDiscountCodeStore, {
      discountObject: 'discountObject',
    }),

    ...mapState(useTravelInsuranceStore, {
      hasTravelInsuranceInCart: 'hasTravelInsuranceInCart',
      travelInsuranceCartItem: 'travelInsuranceCartItem',
    }),
    ...mapState(useLoungePassStore, {
      loungePassItem: 'loungePassItem',
      loungePassTravellersSearchNumber: 'loungePassTravellersSearchNumber',
      displayLoungePassAdditionalTravellers:
        'displayLoungePassAdditionalTravellers',
      isLoungePassAdditionalTravellersDetailsRequired:
        'isLoungePassAdditionalTravellersDetailsRequired',
    }),

    paymentFormTitle(): string {
      if (this.isBookingPortalFlowEnabled) {
        return this.$t('payment.customerDetails');
      } else if (this.hasTravelInsuranceInCart) {
        return this.$t('searchExtras.travelInsurance.mainTravellerDetails');
      }
      return this.title || this.$t('payment.yourDetails');
    },

    /**
     * Determines if user email input is required.
     * True when not on lounge portal, or when payment is needed on lounge portal.
     */
    requiresUserEmailInput(): boolean {
      return this.paymentNeeded;
    },

    emailError(): any {
      // @ts-ignore
      if (
        !this.$v.emailConfirmation.sameAs &&
        !this.$v.userDetails.email.$error
      ) {
        // @ts-ignore
        return this.$v.emailConfirmation;
      }
      // @ts-ignore
      return this.$v.userDetails.email;
    },

    displayEmailErrorMessage(): boolean {
      // @ts-ignore
      return (
        this.$v.userDetails.email.$error && this.$v.emailConfirmation.noPaste
      );
    },

    /**
     * Determines whether to show the payment gateway section.
     * It is shown when not in booking portal flow, payment config has been received
     * and total payment amount is more than 0.
     */
    displayPaymentGateway(): boolean {
      return (
        !this.isBookingPortalFlowEnabled &&
        this.paymentsConfigReceived &&
        this.paymentNeeded
      );
    },

    displayBraintreeGateway(): boolean {
      return (
        this.displayPaymentGateway &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.BRAINTREE
      );
    },

    displayStripeGateway(): boolean {
      return (
        this.displayPaymentGateway &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE
      );
    },

    displayWindcaveGateway(): boolean {
      return (
        this.displayPaymentGateway &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.WINDCAVE
      );
    },

    displayAdvamGateway(): boolean {
      return (
        this.displayPaymentGateway &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.ADVAM
      );
    },

    displayOutboundFlightLookup() {
      const isVisible = this.checkoutForm_checkFieldVisibility(
        'outbound_flight_number',
      );
      if (getFeatureFlag(FEATURE_FLAGS.EX_847_MOVE_FLIGHT_LOOKUP_TO_STEP_4)) {
        return isVisible;
      }
      return isVisible && this.flightWasSelected && this.flightNumber;
    },

    displayInboundFlightLookup() {
      return this.checkoutForm_checkFieldVisibility('inbound_flight_number');
    },

    displayInfoMessage(): boolean {
      return (
        // @ts-ignore - Mixin method
        this.checkoutForm_paymentMethod ===
          this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL &&
        this.paymentNeeded
      );
    },

    iconArrowClass(): object {
      return { 'transform rotate-180': this.showDiscountCode };
    },

    submitButtonLabel(): VueI18n.TranslateResult {
      if (!this.paymentNeeded) {
        return this.$t('UI.button.payForBooking');
      }

      // @ts-ignore - Mixin method
      if (
        this.checkoutForm_paymentMethod ===
        this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL
      ) {
        return this.$t('UI.button.paypal');
      }

      if (this.isBookingPortalFlowEnabled) {
        return this.$t('UI.button.makeBooking');
      }
      return this.$t('UI.button.payForBooking');
    },

    submitButtonId(): string {
      // @ts-ignore - Mixin method
      if (
        this.checkoutForm_paymentMethod ===
        this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL
      ) {
        return 'GAPaymentSubmitPaypal';
      }
      return 'GAPaymentSubmitCard';
    },

    submitButtonDisabled(): boolean {
      // @ts-ignore
      return this.$v.$error || this.disableButton;
    },

    paymentNeeded(): boolean {
      return this.orderTotal !== 0;
    },

    isDiscountFieldEnabled(): boolean {
      return getAppVariable(
        'discount_visibility_rules.is_enabled_in_payment_page',
      );
    },

    userMarketingSubscription() {
      return this.readUserMarketingSubscription;
    },

    marketingStrategy() {
      return this.readMarketingStrategy;
    },

    partnerMarketingPreferences() {
      return this.readMarketingPreferences;
    },

    cartItemsType() {
      return this.readCartItemsType;
    },

    isBookerEmailCheckboxDisabled() {
      return !(
        this.userDetails.booker_email && this.userDetails.booker_email.length
      );
    },

    isCustomerEmailCheckboxDisabled() {
      return !(this.userDetails.email && this.userDetails.email.length);
    },

    isOthersEmailCheckboxDisabled() {
      return !(
        this.userDetails.others_email && this.userDetails.others_email.length
      );
    },

    licensePlateSuccess() {
      if (this.displayVehicleLookupButton) {
        return this.vehicleLookUp.success;
      }
      return this.checkSuccess(
        this.$v.userDetails.license_plate,
        this.userDetails.license_plate,
        this.checkoutForm_checkIfFieldRequired('plate_number'),
      );
    },

    isVehicleRoadworthinessAndWeightCheckboxesDisabled(): boolean {
      if (
        this.displayVehicleLookupButton &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled
      ) {
        return !this.vehicleLookUp.success;
      }
      return false;
    },

    hideChangeVehicleInfo(): boolean {
      return (
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        (this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') ||
          this.checkoutForm_checkFieldVisibility('max_vehicle_weight'))
      );
    },

    /**
     * Returns the email data from Vuex store userInfo
     */
    getUserInfoEmail() {
      return this.getAllUserDetails.email;
    },

    /**
     * Returns the first name data from Vuex store userInfo
     */
    getUserInfoFirstName() {
      return this.getAllUserDetails.firstName;
    },

    /**
     * Returns the last name data from Vuex store userInfo
     */
    getUserInfoLastName() {
      return this.getAllUserDetails.lastName;
    },

    /**
     * Returns the postal code data from Vuex store userInfo
     */
    getUserPostalCode() {
      return this.getAllUserDetails.postalCode;
    },

    /**
     * Returns the license plate data from Vuex store userInfo
     */
    getUserInfoLicense() {
      return this.getAllUserDetails.vehicleDetails.registrationNumber;
    },
    /**
     * Returns the vehicle make data from Vuex store userInfo
     */
    getUserInfoVehicleMake() {
      return this.getAllUserDetails.vehicleDetails.make;
    },
    /**
     * Returns the vehicle model data from Vuex store userInfo
     */
    getUserInfoVehicleModel() {
      return this.getAllUserDetails.vehicleDetails.model;
    },
    /**
     * Returns the vehicle colour data from Vuex store userInfo
     */
    getUserInfoVehicleColour() {
      return this.getAllUserDetails.vehicleDetails.colour;
    },

    /**
     * Returns the phone number data from Vuex store userInfo
     */
    getPhoneNumbers() {
      return this.getAllUserDetails.phoneDetails;
    },

    displayVehicleDetails(): boolean {
      return (
        this.checkoutForm_checkFieldVisibility('plate_number') ||
        this.checkoutForm_checkFieldVisibility('number_of_people_in_vehicle') ||
        this.checkoutForm_checkFieldVisibility('vehicle_make') ||
        this.checkoutForm_checkFieldVisibility('vehicle_model') ||
        this.checkoutForm_checkFieldVisibility('vehicle_colour') ||
        this.checkoutForm_checkFieldVisibility('max_vehicle_weight') ||
        this.checkoutForm_checkFieldVisibility('vehicle_roadworthy')
      );
    },

    displayTripDetails(): boolean {
      return (
        this.checkoutForm_checkFieldVisibility('terminal_name') ||
        this.checkoutForm_checkFieldVisibility('cruise_ship_name')
      );
    },

    displayFlightLookup(): boolean {
      return getAppVariable('is_flight_lookup_enabled');
    },

    terminalNameOptions(): {
      id: number | string;
      name: string | VueI18n.TranslateResult;
    }[] {
      return getAppVariable<Terminal[]>('terminals', []);
    },

    computedTerminalName: {
      get(): { id: number | string; name: string | VueI18n.TranslateResult } {
        return (
          this.terminalNameOptions.find(
            (item) => item.name?.toString() === this.userDetails.terminal_name,
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: {
        id: number | string;
        name: string | VueI18n.TranslateResult;
      }): void {
        this.userDetails.terminal_name = val.name?.toString() ?? '';
      },
    },

    terminalNameError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.terminal_name.$error;
    },

    cruiseShipNameMaxLength(): number {
      return 50;
    },

    numberInVehicleOptions(): { id: number | string; name: string }[] {
      const options = [...VEHICLE_OCCUPANTS];
      // Number in vehicle can't be 0
      return options.slice(1);
    },

    numberChildSeatsOptions(): { id: number | string; name: string }[] {
      const options = [...VEHICLE_OCCUPANTS];
      // Number of child seats can't be more than (number in vehicle -1)
      if (this.userDetails.number_of_people_in_vehicle) {
        return options.slice(0, this.userDetails.number_of_people_in_vehicle);
      }
      return [];
    },

    computedNumberInVehicle: {
      get(): { id: number | string; name: string } {
        return (
          this.numberInVehicleOptions.find(
            (item) =>
              item.name ===
              this.userDetails.number_of_people_in_vehicle?.toString(),
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: { id: number; name: string }): void {
        this.userDetails.number_of_people_in_vehicle = parseInt(val.name);
        if (this.userDetails.number_of_people_in_vehicle === 1) {
          this.userDetails.number_of_child_seats = 0;
        } else {
          this.userDetails.number_of_child_seats = null;
        }
      },
    },

    computedNumberChildSeats: {
      get(): { id: number | string; name: string } {
        return (
          this.numberChildSeatsOptions.find(
            (item) =>
              item.name === this.userDetails.number_of_child_seats?.toString(),
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: { id: number; name: string }): void {
        this.userDetails.number_of_child_seats = Number.parseInt(val.name);
      },
    },

    disableChildSeatsDropdown(): boolean {
      return !this.userDetails.number_of_people_in_vehicle;
    },

    numberInVehicleError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.number_of_people_in_vehicle.$error;
    },

    numberChildSeatsError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.number_of_child_seats.$error;
    },

    isVehicleFeatureFlagEnabled(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.PT_2971_VEHICLE_LOOKUP_IMPROVED_UI,
        ) === true
      );
    },

    isLinkToNotAllowedVehiclesDisplayed(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.ECOM_3834_LINK_TO_LIST_OF_NOT_ALLOWED_VEHICLES,
        ) === true
      );
    },

    vehicleFieldsMaxLength(): number {
      return 25;
    },

    displayVehicleLookUp(): boolean {
      return (
        getAppVariable('is_vehicle_lookup_enabled') &&
        this.checkoutForm_checkFieldVisibility('plate_number') &&
        this.isVehicleFeatureFlagEnabled
      );
    },

    displayVehicleLookupButton(): boolean {
      return (
        this.displayVehicleLookUp &&
        (this.checkoutForm_checkFieldVisibility('vehicle_make') ||
          this.checkoutForm_checkFieldVisibility('vehicle_model') ||
          this.checkoutForm_checkFieldVisibility('vehicle_colour'))
      );
    },

    filteredLookupDetails() {
      const details = [
        this.checkoutForm_checkFieldVisibility('vehicle_make')
          ? this.userDetails.vehicle_make
          : null,
        this.checkoutForm_checkFieldVisibility('vehicle_model')
          ? this.userDetails.vehicle_model
          : null,
        this.checkoutForm_checkFieldVisibility('vehicle_colour')
          ? this.userDetails.vehicle_colour
          : null,
      ].filter(Boolean);
      return details.length > 0 ? details : null;
    },

    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },

    isMoveFlightLookupToStep4Enabled(): boolean {
      return (
        getFeatureFlag(FEATURE_FLAGS.EX_847_MOVE_FLIGHT_LOOKUP_TO_STEP_4) ===
        true
      );
    },

    isVehicleRoadworthinessAndWeightEnabled(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.ECOM_3797_VEHICLE_ROADWORTHY_AND_WEIGHT,
        ) === true
      );
    },

    isVehicleRoadworthinessAndWeightValidationEnabled() {
      return (
        getFeatureFlag(
          FEATURE_FLAGS.ECOM_3837_VEHICLE_ROADWORTHY_AND_WEIGHT_VALIDATION,
        ) === true
      );
    },

    singleDiscountMode(): boolean {
      return (
        getFeatureFlag(FEATURE_FLAGS.CRO_1027_DISCOUNT_CODE_PAYMENT_SECTION) ===
        true
      );
    },

    showTopSecurePaymentMessage(): boolean {
      return (
        getFeatureFlag(
          FEATURE_FLAGS.CRO_534_SECURE_PAYMENT_MESSAGE_ON_TOP,
          false,
        ) === true
      );
    },

    isNewCancelationPolicyEnabled(): boolean {
      return (
        getFeatureFlag(FEATURE_FLAGS.CRO_1217_CANCEL_PROTECTION_IMPROVEMENT) ===
        true
      );
    },

    vehicleMaxWeightValid() {
      return (
        this.vehicleLookUp.grossVehicleWeight !== null &&
        this.vehicleLookUp.grossVehicleWeight < 2500
      );
    },

    merchantId() {
      return getAppVariable('review_providers[0].merchant_id');
    },
    feefoReviewsWithinFlow(): boolean {
      return (
        getFeatureFlag(
          FEATURE_FLAGS.CRO_844_FEEFO_REVIEW_WITHIN_FLOW,
          false,
        ) === 'VARIANT_A'
      );
    },

    partnerId(): string {
      return getAppVariable('partner_id');
    },
    loungePassCustomerGroupLabels() {
      if (this.displayLoungePassAdditionalTravellers) {
        return getProductTypeCustomerGroupLabels(PRODUCT_TYPES.LOUNGES);
      }

      return {};
    },
    flightWasSelected(): boolean {
      return (
        this.readCartItems[0].search_criteria[this.cartItemsType]
          ?.is_flight_selected === true
      );
    },
    flightNumber(): string {
      return this.readCartItems[0].search_criteria[this.cartItemsType]
        ?.flight_number;
    },
    showTravelInsuranceAdditionalTravellers() {
      return Boolean(
        this.additionalTravellersDetails.length > 0 &&
        !this.hasAdditionalTravellersCombinedProducts,
      );
    },
    showLoungePassAdditionalTravellers() {
      return Boolean(
        this.displayLoungePassAdditionalTravellers &&
        !this.hasAdditionalTravellersCombinedProducts,
      );
    },
    hasAdditionalTravellersCombinedProducts() {
      return Boolean(
        this.additionalTravellersDetails.length > 0 &&
        this.displayLoungePassAdditionalTravellers &&
        isGtm2025LoungePassAndTravelInsuranceCombinedEnabled(),
      );
    },
    additionalTravellersCombinedProducts() {
      const products: AdditionalTravellerProduct[] = [];
      if (this.hasAdditionalTravellersCombinedProducts) {
        if (this.hasTravelInsuranceInCart) {
          const isValid = !this.$v.additionalTravellersDetails.$invalid;
          const isDirty = this.$v.additionalTravellersDetails.$dirty;
          const validationMessage = isValid
            ? this.$t(
                'additionalTravellersCombinedProducts.productValidationMessages.completed',
              )
            : `${this.additionalTravellersDetails.length} ${this.$t('additionalTravellersCombinedProducts.productValidationMessages.travellerDetailsRequired')}`;
          products.push({
            title: this.$t(
              'additionalTravellersCombinedProducts.productTitles.travelInsurance',
            ),
            isValid,
            isDirty,
            validationMessage,
          });
        }
        if (this.displayLoungePassAdditionalTravellers) {
          const isValid =
            !this.$v.loungePassAdditionalTravellersDetails.$invalid;
          const isDirty = this.$v.loungePassAdditionalTravellersDetails.$dirty;
          const validationMessage = isValid
            ? this.$t(
                'additionalTravellersCombinedProducts.productValidationMessages.completed',
              )
            : `${this.loungePassAdditionalTravellersDetails.length} ${this.$t('additionalTravellersCombinedProducts.productValidationMessages.travellerDetailsRequired')}`;
          products.push({
            title: this.$t(
              'additionalTravellersCombinedProducts.productTitles.lounge',
            ),
            isValid,
            isDirty,
            validationMessage,
          });
        }
      }
      return products;
    },

    isPostalAreaCodeMandatoryField(): boolean {
      return (
        getFeatureFlag(FEATURE_FLAGS.CRO_1176_POSTAL_CODE_MANDATORY_FIELD) ===
        true
      );
    },

    zipcodeLabel(): string {
      return this.isPostalAreaCodeMandatoryField
        ? 'Postal area code (e.g. M90)'
        : (this.$t('UI.form.zipCode.label') as string);
    },

    zipcodeMaxLength(): number {
      return this.isPostalAreaCodeMandatoryField ? 4 : 8;
    },

    zipcodeRequiredStar(): boolean {
      return this.isPostalAreaCodeMandatoryField
        ? true
        : this.checkoutForm_checkIfFieldRequired('zipcode');
    },

    zipcodeErrorMessages(): Record<string, string> {
      return this.isPostalAreaCodeMandatoryField
        ? {
            required: 'Please enter postal area code.',
            minLength:
              'Postal area code must be at least 2 characters (e.g. M1).',
            postalAreaCode: 'Wrong format for Postal area code',
          }
        : this.checkoutForm_validationMessages.zipcode;
    },
  },

  watch: {
    'userDetails.zipcode'(val: string) {
      if (this.isPostalAreaCodeMandatoryField && val) {
        const normalized = val.trim().toUpperCase();
        if (normalized !== val) {
          this.userDetails.zipcode = normalized;
        }
      }
    },

    userDetails: {
      deep: true, // watch nested data properties
      handler(formData: UserData) {
        this.commitStoreFormData({ formData });

        // Uncheck recipients checkbox if email value is empty
        if (!formData.email) {
          this.userDetails.send_customer_email = false;
        }
        if (!formData.others_email) {
          this.userDetails.send_others_email = false;
        }

        formData.license_plate = formData.license_plate.replace(/\s/g, '');
      },
    },

    vehicleLookUp: {
      deep: true,
      handler() {
        this.commitStoreVehicleLookUpState(this.vehicleLookUp);
      },
    },

    travelInsuranceCartItem: {
      handler(newItem) {
        if (newItem?.search_criteria?.travel_insurance?.additional_travellers) {
          const storedTravellers =
            this.readPaymentFormData?.additionalTravellers || [];

          this.additionalTravellersDetails =
            newItem.search_criteria.travel_insurance.additional_travellers.map(
              (traveller, index) => ({
                ...traveller,
                firstName: storedTravellers[index]?.firstName || '',
                lastName: storedTravellers[index]?.lastName || '',
              }),
            );
        } else {
          this.additionalTravellersDetails = [];
        }
      },
      immediate: true,
      deep: true,
    },

    additionalTravellersDetails: {
      deep: true,
      handler(travellers) {
        this.commitStoreFormData({
          formData: {
            ...this.readPaymentFormData,
            additionalTravellers: travellers.map(({ firstName, lastName }) => ({
              firstName,
              lastName,
            })),
          },
        });
      },
    },

    loungePassItem: {
      handler(newItem) {
        if (this.displayLoungePassAdditionalTravellers) {
          this.setLoungePassAdditionalTravellersDetails();
          const storedTravellers =
            this.readPaymentFormData?.loungePassAdditionalTravellers || [];
          if (
            storedTravellers.length > 0 &&
            this.loungePassTravellersSearchNumber >=
              this.readPaymentFormData?.loungePassTravellersSearchNumber
          ) {
            this.loungePassAdditionalTravellersDetails =
              this.loungePassAdditionalTravellersDetails.map(
                (traveller, index) => {
                  return {
                    ...traveller,
                    firstName: storedTravellers[index]?.firstName || '',
                    lastName: storedTravellers[index]?.lastName || '',
                  };
                },
              );
          }

          this.commitStoreFormData({
            formData: {
              ...this.readPaymentFormData,
              loungePassTravellersSearchNumber:
                this.loungePassTravellersSearchNumber,
            },
          });
        }
      },
      immediate: true,
      deep: true,
    },

    loungePassAdditionalTravellersDetails: {
      deep: true,
      handler(travellers) {
        this.commitStoreFormData({
          formData: {
            ...this.readPaymentFormData,
            loungePassAdditionalTravellers: travellers.map(
              ({ firstName, lastName }) => ({ firstName, lastName }),
            ),
          },
        });
      },
    },
  },

  async mounted() {
    if (this.$auth.user) {
      // If user is logged in retrieve data from userInfo store to populate fields
      if (!this.isBookingPortalFlowEnabled) {
        await this.getUserData();
      }
    } else {
      this.loadStoredData();
    }

    this.loadStoredVehicleLookUpState();

    // Always start the terminal field empty so nothing is pre-selected.
    if (this.checkoutForm_checkFieldVisibility('terminal_name')) {
      this.userDetails.terminal_name = '';
    }

    this.onEmailChangeDebounced = debounce(
      this.handleEmailChange,
      TEXT_INPUT_DEBOUNCE_TIME,
    );

    if (this.isBookingPortalFlowEnabled) {
      this.handleEmailBookerChange();
    }

    if (
      this.displayFlightLookup &&
      (this.getSearchCriteria('arrival') || this.getSearchCriteria('departure'))
    ) {
      if (this.checkoutForm_checkFieldVisibility('inbound_flight_number')) {
        await this.fetchFlightLookupDetails({
          ...this.getSearchCriteria('arrival'),
          direction: 'arrival',
        });
      }
      if (this.checkoutForm_checkFieldVisibility('outbound_flight_number')) {
        await this.fetchFlightLookupDetails({
          ...this.getSearchCriteria('departure'),
          direction: 'departure',
        });
      }

      this.prefillOutboundFlight();
    }
    if (
      !this.displayFlightLookup &&
      this.flightWasSelected &&
      !this.flightNumber
    ) {
      this.selectOutboundFlightLookup({ id: null });
    }

    if (this.singleDiscountMode && this.discountObject?.code) {
      this.showDiscountCode = true;
    }

    trackLaunchDarklyExperimentImpression(
      this.$gtm,
      FEATURE_FLAGS.CRO_844_FEEFO_REVIEW_WITHIN_FLOW,
      'cro-844',
      'CONTROL',
    );
    trackLaunchDarklyExperimentImpression(
      this.$gtm,
      FEATURE_FLAGS.CRO_1217_CANCEL_PROTECTION_IMPROVEMENT,
      'cro-1217',
    );
    trackLaunchDarklyExperimentImpression(
      this.$gtm,
      FEATURE_FLAGS.CRO_1176_POSTAL_CODE_MANDATORY_FIELD,
      'cro-1176',
    );
  },

  methods: {
    ...mapActions(useUserInfoStore, {
      fetchUserDetails: 'fetchUserDetails',
      fetchVehicleDetails: 'fetchVehicleDetails',
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeInboundFlightLookup: 'selectInboundFlightLookup',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),

    ...mapActions(useMarketingPreferencesStore, {
      dispatchUserMarketingSubscription: 'dispatchUserMarketingSubscription',
    }),

    ...mapActions(useCartCheckoutStore, {
      commitCartBundle: 'commitCartBundle',
    }),

    ...mapActions(useDiscountCodeStore, {
      validateDiscountCode: 'validateDiscountCode',
      removeDiscountCode: 'removeDiscountCode',
    }),

    ...mapActions(usePaymentFormStore, {
      commitClearFormData: 'commitClearFormData',
      commitStoreFormData: 'commitStoreFormData',
      commitStoreVehicleLookUpState: 'commitStoreVehicleLookUpState',
    }),

    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),

    prefillOutboundFlight(): void {
      if (this.flightWasSelected) {
        if (this.flightNumber) {
          const iata =
            this.readCartItems[0].search_criteria[this.cartItemsType]
              .airline_iata;
          const flightId =
            iata && this.flightNumber ? `${iata}-${this.flightNumber}` : null;
          const selectedOutboundFlight = flightId
            ? this.findOutboundFlightById(flightId)
            : { id: CANT_FIND_FLIGHT_ID };
          this.selectOutboundFlightLookup(selectedOutboundFlight);
        } else {
          this.selectOutboundFlightLookup({ id: CANT_FIND_FLIGHT_ID });
        }
      }
    },

    selectOutboundFlightLookup(flightLookup): void {
      this.userDetails.outbound_flight = flightLookup?.hasOwnProperty('id')
        ? flightLookup.id
        : '';
      this.userDetails.outbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.outbound_airport = flightLookup?.airport ?? '';
      this.userDetails.outbound_terminal = flightLookup?.terminalName ?? '';
      this.storeOutboundFlightLookup(flightLookup);
    },
    selectInboundFlightLookup(flightLookup): void {
      this.userDetails.inbound_flight = flightLookup?.hasOwnProperty('id')
        ? flightLookup.id
        : '';
      this.userDetails.inbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.inbound_airport = flightLookup?.airport ?? '';
      this.userDetails.inbound_terminal = flightLookup?.terminalName ?? '';
      this.storeInboundFlightLookup(flightLookup);
    },

    onFormFieldBlur(field: string): void {
      if (field === 'license_plate') {
        this.vehicleLookUp.error = false;
      }

      if (field === 'first_name' || field === 'last_name') {
        const name = this.userDetails[field];
        if (name) {
          const normalized = name
            .normalize('NFC')
            .trim()
            // Replace multiple whitespace characters with a single space.
            .replace(/\s+/g, ' ')
            // Normalize all apostrophe variants to a straight apostrophe.
            .replace(/[\u2018\u2019\u02BC\u2032\uFF07`\u00B4]/g, "'")
            // Normalize all hyphen/dash characters (including the minus sign) to a standard hyphen.
            .replace(/[\p{Pd}\u2212]/gu, '-');
          if (normalized !== name) {
            this.userDetails[field] = normalized;
          }
        }
      }

      if (field.startsWith('email') && this.onEmailChangeDebounced) {
        this.onEmailChangeDebounced();
      }

      if (field === 'emailConfirmation') {
        this.$v[field].$touch();
        this.userDetails.is_customer_email_confirmed =
          this.userDetails.email === this.emailConfirmation;
      } else {
        this.$v.userDetails[field].$touch();
      }

      if (field === 'email') {
        this.userDetails.is_customer_email_confirmed =
          this.userDetails.email === this.emailConfirmation;
      }
    },

    onPaste(): void {
      this.attemptToPaste = true;
      // @ts-ignore
      this.$v.emailConfirmation.$touch();
      setTimeout(() => {
        this.attemptToPaste = false;
        // @ts-ignore
        if (!this.$v.$error) {
          // @ts-ignore
          this.$v.emailConfirmation.$reset();
        }
      }, 3000);
    },

    handleUserMarketingChannelsChange(
      marketingChannels: UserData['marketing_channels'],
    ): void {
      this.userDetails.marketing_channels = marketingChannels;
    },

    handleEmailBookerChange(): void {
      const userDetails = this.$auth;
      this.userDetails.booker_email = userDetails.user.email;
    },

    handleEmailChange() {
      // @ts-ignore - no access to $v
      if (
        !this.$v.userDetails.email.$invalid &&
        this.userDetails.email === this.emailConfirmation
      ) {
        let customerUUID: string | undefined;

        this.dispatchUserMarketingSubscription(this.userDetails.email)
          .then((data) => {
            // Check if new user
            this.isNewUser = data === null;

            if (this.userDetails.marketing_channels) {
              this.updateDefaultMarketingChannelsUserValues();

              /**
               * Toggles the expansion state of the marketing pref dropdown
               * based on the availability of the phone number field and user preferences.
               */
              if (!this.isNewUser) {
                const { sms, email } = this.userDetails.marketing_channels;
                const phoneNumberFieldVisible =
                  this.checkoutForm_checkFieldVisibility('phone_number');
                this.showDropdownExpanded = phoneNumberFieldVisible
                  ? !(sms && email)
                  : !email;

                customerUUID = this.readCustomerUUID;
              }
            }

            this.showMarketingPreferences = true;
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            trackUserIdentity(this.$gtm, this.userDetails.email, customerUUID);
          });
      }
    },
    /**
     * Set default marketing preferences according to
     * opt-in/opt-out startegy for new user and already saved settings
     * for an old user
     */
    updateDefaultMarketingChannelsUserValues() {
      const marketingChannels: UserData['marketing_channels'] = {};
      const optIn = this.marketingStrategy === 'opt-in';
      const savedUserMarketingChannels =
        this.userMarketingSubscription?.marketing_channels;

      if (this.isMarketingChannelAvailable('email')) {
        marketingChannels.email = this.isNewUser
          ? !optIn
          : !!savedUserMarketingChannels?.email;
      }

      if (this.isMarketingChannelAvailable('sms')) {
        marketingChannels.sms = this.isNewUser
          ? !optIn
          : !!savedUserMarketingChannels?.sms;
      }

      this.userDetails = {
        ...this.userDetails,
        marketing_channels: marketingChannels,
      };
    },

    isMarketingChannelAvailable(channel: string): boolean {
      return !!this.partnerMarketingPreferences?.marketing_channels?.includes(
        channel,
      );
    },

    /*
     * Samuel Bruton
     * Confirming with another dev on what this actually is being used for.
     * Looks like it can be removed.
     * */
    loadStoredData(): void {
      const formData = this.readPaymentFormData;
      if (formData) {
        this.userDetails = {
          ...formData,
          // @ts-ignore mixin method
          phone: this.checkoutForm_checkFieldVisibility('phone_number')
            ? { ...formData.phone }
            : { ...this.userDetails.phone },
          // @ts-ignore mixin method
          license_plate: this.checkoutForm_checkFieldVisibility('plate_number')
            ? formData.license_plate
            : '',
          // @ts-ignore mixin method
          zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
            ? formData.zipcode
            : '',
          // @ts-ignore mixin method
          outbound_flight: this.checkoutForm_checkFieldVisibility(
            'outbound_flight_number',
          )
            ? formData.outbound_flight
            : '',
          // @ts-ignore mixin method
          inbound_flight: this.checkoutForm_checkFieldVisibility(
            'inbound_flight_number',
          )
            ? formData.inbound_flight
            : '',
          // @ts-ignore mixin method
          number_of_people_in_vehicle: this.checkoutForm_checkFieldVisibility(
            'number_of_people_in_vehicle',
          )
            ? formData.number_of_people_in_vehicle
            : null,
          // @ts-ignore mixin method
          number_of_child_seats: this.checkoutForm_checkFieldVisibility(
            'is_child_seat_required',
          )
            ? formData.number_of_child_seats
            : null,
          vehicle_make: this.checkoutForm_checkFieldVisibility('vehicle_make')
            ? formData.vehicle_make
            : '',
          vehicle_model: this.checkoutForm_checkFieldVisibility('vehicle_model')
            ? formData.vehicle_model
            : '',
          vehicle_colour: this.checkoutForm_checkFieldVisibility(
            'vehicle_colour',
          )
            ? formData.vehicle_colour
            : '',
          // @ts-ignore mixin method
          cruise_ship_name: this.checkoutForm_checkFieldVisibility(
            'cruise_ship_name',
          )
            ? formData.cruise_ship_name
            : '',
          // @ts-ignore mixin method
          terminal_name: this.checkoutForm_checkFieldVisibility('terminal_name')
            ? formData.terminal_name
            : '',
        };
        if (formData.is_customer_email_confirmed) {
          this.emailConfirmation = formData.email;
        }
      }
    },

    loadStoredVehicleLookUpState() {
      const vehicleLookUp = this.readVehicleLookUpState;
      if (vehicleLookUp) {
        this.vehicleLookUp = {
          ...vehicleLookUp,
          loading: false,
          success: this.displayVehicleLookupButton
            ? vehicleLookUp.success
            : false,
        };
      }
    },

    /** API call to get the user data */
    async getUserData(): Promise<void> {
      const token = await this.$auth.getToken();
      if (token) {
        await this.fetchUserDetails(token)
          .then(() => {
            const formData = this.readPaymentFormData ?? ({} as UserData);

            const getDataFromUserOrStore = (
              field: string,
              loggedInUserData: string,
            ) => {
              return formData[field] ? formData[field] : loggedInUserData;
            };

            this.userDetails = {
              ...formData,
              email: getDataFromUserOrStore('email', this.getUserInfoEmail),
              first_name: getDataFromUserOrStore(
                'first_name',
                this.getUserInfoFirstName,
              ),
              last_name: getDataFromUserOrStore(
                'last_name',
                this.getUserInfoLastName,
              ),
              // @ts-ignore mixin method
              license_plate: this.checkoutForm_checkFieldVisibility(
                'plate_number',
              )
                ? getDataFromUserOrStore(
                    'plate_namber',
                    this.getUserInfoLicense,
                  )
                : '',
              // @ts-ignore mixin method
              zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
                ? getDataFromUserOrStore('zipcode', this.getUserPostalCode)
                : '',
              // @ts-ignore mixin method
              phone: this.checkoutForm_checkFieldVisibility('phone_number')
                ? formData?.phone?.phone_number
                  ? { ...formData.phone }
                  : {
                      phone_number: this.getPhoneNumbers.phoneNumber,
                      formatted_number:
                        this.getPhoneNumbers.phoneNumberWithDialingCode,
                      country_code: this.getPhoneNumbers.dialingCode,
                      country_iso_code: this.getPhoneNumbers.isoCode,
                    }
                : {
                    // empty data needed otherwise api call will fail
                    phone_number: '',
                    formatted_number: '',
                    country_code: '',
                    country_iso_code: '',
                  },
              // @ts-ignore mixin method
              outbound_flight:
                this.checkoutForm_checkFieldVisibility(
                  'outbound_flight_number',
                ) && formData?.outbound_flight
                  ? formData.outbound_flight
                  : '',
              // @ts-ignore mixin method
              inbound_flight:
                this.checkoutForm_checkFieldVisibility(
                  'inbound_flight_number',
                ) && formData?.inbound_flight
                  ? formData.inbound_flight
                  : '',
              // @ts-ignore mixin method
              number_of_people_in_vehicle:
                this.checkoutForm_checkFieldVisibility(
                  'number_of_people_in_vehicle',
                ) && formData?.number_of_people_in_vehicle
                  ? formData.number_of_people_in_vehicle
                  : null,
              // @ts-ignore mixin method
              number_of_child_seats:
                this.checkoutForm_checkFieldVisibility(
                  'is_child_seat_required',
                ) && formData?.number_of_child_seats
                  ? formData.number_of_child_seats
                  : null,
              marketing_channels: this.partnerMarketingPreferences,
              // @ts-ignore mixin method
              vehicle_make: this.checkoutForm_checkFieldVisibility(
                'vehicle_make',
              )
                ? getDataFromUserOrStore(
                    'vehicle_make',
                    this.getUserInfoVehicleMake,
                  )
                : '',
              // @ts-ignore mixin method
              vehicle_model: this.checkoutForm_checkFieldVisibility(
                'vehicle_model',
              )
                ? getDataFromUserOrStore(
                    'vehicle_model',
                    this.getUserInfoVehicleModel,
                  )
                : '',
              // @ts-ignore mixin method
              vehicle_colour: this.checkoutForm_checkFieldVisibility(
                'vehicle_colour',
              )
                ? getDataFromUserOrStore(
                    'vehicle_colour',
                    this.getUserInfoVehicleColour,
                  )
                : '',
              // @ts-ignore mixin method
              cruise_ship_name:
                this.checkoutForm_checkFieldVisibility('cruise_ship_name') &&
                formData?.cruise_ship_name
                  ? formData.cruise_ship_name
                  : '',
              // @ts-ignore mixin method
              terminal_name:
                this.checkoutForm_checkFieldVisibility('terminal_name') &&
                formData?.terminal_name
                  ? formData.terminal_name
                  : '',
            };
            if (formData.is_customer_email_confirmed) {
              this.emailConfirmation = this.userDetails.email;
            }
          })
          .catch((error: Error) => {
            console.error(
              'Error with fetchUserDetails. Error logged with Datadog.',
            );
            datadogRum.addError(error);
          });
      }
    },

    /**
     * Handles form validation errors by expanding invalid travellers and scrolling to the first error.
     */
    handleFormValidationErrors(): void {
      // Check for invalid travellers, and expanding each that failed validation
      if (
        this.$v.additionalTravellersDetails.$invalid &&
        this.$refs.additionalTravellersComponent
      ) {
        this.$refs.additionalTravellersComponent.expandInvalidTravellers();
      }

      // Scroll to the first field with an error
      smoothScroll('.input-container--error');
    },

    validateForm(): void {
      if (!this.hideChangeVehicleInfo) {
        this.vehicleLookUp.enterManually = true;
      }

      this.$v.$touch();

      if (this.$v.$error) {
        this.handleFormValidationErrors();
        return;
      }

      if (!this.displayWindcaveGateway && !this.displayAdvamGateway) {
        this.setSubmitButtonDisableState(true);
      }

      if (!this.paymentNeeded || this.isBookingPortalFlowEnabled) {
        // @ts-ignore - Mixin method
        this.checkoutForm_handlePaymentCompleted({
          payment_method_id: 'zero',
          merchant_id: 'zero',
        });
        this.setSubmitButtonDisableState(false);
      } else {
        // @ts-ignore
        this.$refs.paymentGateway.submitForm();
      }
    },
    /**
     * Validates the form and displays error messages without submitting.
     * Used by wallet payment methods (Apple Pay, Google Pay) to show validation
     * errors when the form is invalid before they attempt payment authorization.
     */
    showErrorIfFormInvalid(): void {
      this.$v.$touch();

      if (this.$v.$error) {
        this.handleFormValidationErrors();
      }
    },

    addDiscount(val: string): void {
      this.validatingDiscount = true;
      this.setSubmitButtonDisableState(true);

      const params = {
        cartToken: this.cartToken,
        code: val.replace(/[/\\]/g, '|'),
      };

      this.validateDiscountCode(params)
        .then(({ orderTotals, items, bundle }: ParsedDiscount) => {
          if (bundle) {
            this.commitCartBundle({
              ...bundle,
              items: [...items.filter((item) => item.bundle_id === bundle.id)],
            });
          }
          this.$emit('updateItems', items);
          this.$emit('updateOrderTotals', orderTotals);
          this.handleWidgetReload();
          this.discountError = '';

          if (!this.displayWindcaveGateway) {
            this.setSubmitButtonDisableState(false);
          }
        })
        .catch((e: Error) => {
          this.$emit('handleAddDiscountError');
          const errorResponse = JSON.parse(e.message);
          this.discountError = errorResponse.body.message;
          this.setSubmitButtonDisableState(false);
        })
        .finally(() => {
          this.validatingDiscount = false;
        });
    },

    removeDiscount(val: string): void {
      this.validatingDiscount = true;
      this.setSubmitButtonDisableState(true);

      const params = {
        cartToken: this.cartToken,
        code: val,
      };

      this.removeDiscountCode(params)
        .then(({ orderTotals, items, bundle }: ParsedDiscount) => {
          if (bundle) {
            this.commitCartBundle({
              ...bundle,
              items: [...items.filter((item) => item.bundle_id === bundle.id)],
            });
          }
          this.$emit('updateItems', items);
          this.$emit('updateOrderTotals', orderTotals);
          this.handleWidgetReload();

          this.discountError = '';
        })
        .catch((e: Error) => {
          const errorResponse = JSON.parse(e.message);
          this.discountError = errorResponse.body.message;
        })
        .finally(() => {
          this.validatingDiscount = false;

          if (!this.displayWindcaveGateway) {
            this.setSubmitButtonDisableState(false);
          }
        });
    },
    /**
     * Displays payment decline error message and reload the widget for Windcave and Advam PSPs
     */
    errorHandler(): void {
      this.setSubmitButtonDisableState(false);
      this.$emit('error');
      this.handleWidgetReload();
    },
    /**
     * Displays technical error message and reload the widget for Windcave and Advam PSPs
     */
    showTechnicalError(): void {
      this.commitUpdatePreloader('');
      this.setSubmitButtonDisableState(false);
      this.$emit('show-technical-error');
      this.$emit('reloadPaymentWidget');
    },

    /**
     * Cancels the current transaction by resetting the loading state,
     * reloading the payment widget and cancelling booking creation
     * Currently used for Advam when Google Pay / Apple Pay is cancelled by the user
     */
    cancelTransaction(): void {
      this.commitUpdatePreloader('');
      this.setSubmitButtonDisableState(false);
      this.$emit('reloadPaymentWidget');
      this.$emit('cancel-booking');
    },

    /**
     * Handles the payment widget reload for Windcave and Advam PSPs.
     * These PSPs require calling /client-config to obtain a new session ID whenever
     * the price is updated (e.g., discount added/removed) or an error occurs,
     * as their widgets initialize only with a session ID.
     */
    handleWidgetReload(): void {
      if (this.displayWindcaveGateway || this.displayAdvamGateway || this.displaySycurioGateway()) {
        this.$emit('reloadPaymentWidget');
      }
    },

    addCancellationProtection(): void {
      this.$emit('add-cancellation-protection');

      if (this.displayWindcaveGateway) {
        this.setSubmitButtonDisableState(true);
      }
    },

    removeCancellationProtection(): void {
      this.$emit('remove-cancellation-protection');

      if (this.displayWindcaveGateway) {
        this.setSubmitButtonDisableState(true);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkSuccess(
      validation: any,
      value: string | number | null,
      isRequired: boolean,
    ): boolean {
      if (isRequired) return validation.$anyDirty && !validation.$anyError;
      /**
       * For fields with only requiredIf=false validation (ex. zipcode) - validation.$anyError will always be false.
       * Extra check for the field value is required to prevent displaying the success state for an empty field.
       */
      return validation.$anyDirty && !validation.$anyError && !!value;
    },

    /**
     * Updates the state of submit button
     * true - the button is disabled
     * false - the button is enabled
     * @param {boolean} value
     */
    setSubmitButtonDisableState(value: boolean): void {
      this.disableButton = value;
    },

    getSearchCriteria(direction: string): string {
      const date =
        direction === 'arrival'
          ? this.readCartItems[0].search_criteria[this.cartItemsType].date2
          : this.readCartItems[0].search_criteria[this.cartItemsType].date1;
      const time =
        direction === 'arrival'
          ? this.readCartItems[0].search_criteria[this.cartItemsType].time2
          : this.readCartItems[0].search_criteria[this.cartItemsType].time1;
      const airportCode =
        this.readCartItems[0].search_criteria[this.cartItemsType].airport_code;
      const dateTimeISO = `${date}T${time}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      return {
        datetime: format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc),
        airportCode,
      };
    },

    getDateOfBirth(dob: string): string {
      const dateFormat =
        this.$i18n.locale === 'en-US'
          ? DATE_TIME_FORMATS.month_day_full_year_slash_spaced
          : DATE_TIME_FORMATS.day_month_full_year_slash_spaced;

      return format(new Date(dob), dateFormat);
    },

    changeVehicle() {
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.showDetails = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthinessAndWeightError = '';
      this.$nextTick(() => {
        this.$refs.licensePlateTextField.$el.focus();
      });
    },

    async vehicleLookUpSubmit() {
      this.vehicleLookUp.error = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.grossVehicleWeight = null;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyValid = false;
      this.vehicleLookUp.vehicleRoadworthinessAndWeightError = '';
      // Validate license plate
      this.$v.userDetails.license_plate.$touch();
      if (this.$v.userDetails.license_plate.$error) return;
      this.vehicleLookUp.loading = true;
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.enterManually = false;
      this.vehicleLookUp.showDetails = false;
      try {
        const params = [
          this.userDetails.license_plate,
          this.isVehicleRoadworthinessAndWeightEnabled &&
            this.checkoutForm_checkFieldVisibility('vehicle_roadworthy'),
          this.isVehicleRoadworthinessAndWeightEnabled &&
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight'),
        ];
        const details = await this.fetchVehicleDetails(...params);
        this.userDetails.vehicle_make = details.make;
        this.userDetails.vehicle_model = details.model;
        this.userDetails.vehicle_colour = details.colour;

        if (this.isVehicleRoadworthinessAndWeightValidationEnabled) {
          this.vehicleLookUp.grossVehicleWeight = details.grossVehicleWeight;
          this.vehicleLookUp.vehicleRoadworthyValid =
            details.motStatus === 'Y' || details.motStatus === 'X';

          if (
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight') &&
            details.grossVehicleWeight === null
          ) {
            datadogRum.addError('Vehicle validation error: car weight is null');
          }

          if (
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight') &&
            details.grossVehicleWeight > 2500
          ) {
            datadogRum.addError(
              'Vehicle validation error: Vehicle exceeds allowed weight',
            );
          }

          if (
            this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') &&
            details.motStatus !== 'Y' &&
            details.motStatus !== 'X'
          ) {
            datadogRum.addError(
              'Vehicle validation error: Vehicle did not have a valid MOT',
            );
          }
        }

        this.vehicleLookUp.showDetails = true;
        this.vehicleLookUp.success = true;
        this.vehicleLookUp.enterManually = false;
        // this.licensePlateNumberFieldDisabled = true;
        addCustomMetric(
          'tracking',
          'Vehicle Lookup: Success',
          {
            partner_id: this.partnerId,
          },
          null,
          'vehicle_lookup',
        );
      } catch (error) {
        addCustomMetric(
          'error',
          'Vehicle Lookup: Error',
          {
            partner_id: this.partnerId,
            error: error instanceof Error ? error.message : String(error),
          },
          null,
          'vehicle_lookup',
        );
        this.vehicleLookUp.showDetails = false;

        if (
          this.isVehicleRoadworthinessAndWeightValidationEnabled &&
          (this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') ||
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight'))
        ) {
          this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
            'Something went wrong, please try again';

          datadogRum.addError('Vehicle validation error: API request failed');
        } else {
          this.vehicleLookUp.error = true;
          this.vehicleLookUp.enterManually = true;
        }
      } finally {
        this.vehicleLookUp.loading = false;
      }
    },

    keydownSpaceHandler(e: KeyboardEvent) {
      if (e.target instanceof HTMLElement) {
        e.target.click();
      }
    },

    searchAgain(): void {
      goToApp(
        getDomainUrl(DOMAINS_NAMES.ECOMMERCE, this.$launchDarkly),
        MANAGE_BOOKING_URLS.BOOKING_SEARCH,
      );
    },

    vehicleMaxWeightClickHandler(
      value: boolean,
      event: HTMLInputElement,
    ): void {
      if (
        this.displayVehicleLookupButton &&
        !this.vehicleMaxWeightValid &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        this.checkoutForm_checkFieldVisibility('plate_number')
      ) {
        if (event?.target instanceof HTMLInputElement) {
          event.target.checked = false;
        }

        this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
          'Weight validation failed';

        const header =
          this.vehicleLookUp.grossVehicleWeight === null
            ? "Sorry, we were unable to validate the vehicle's weight."
            : 'Sorry, the vehicle is over the allowance for this car park.';
        const btnText =
          this.vehicleLookUp.grossVehicleWeight === null
            ? 'Select different product'
            : 'Search again';

        this.$openModal('GlobalModalError', {
          header,
          body: 'Please try searching again and select a different option.',
          buttons: [
            {
              callback: this.changeVehicle,
              btnText: 'Change licence plate',
            },
            {
              callback: this.searchAgain,
              btnText,
            },
          ],
        })
          .then((callback) => {
            if (callback && typeof callback === 'function') {
              callback();
            }
          })
          .catch(() => {});
      } else {
        this.vehicleLookUp.vehicleMaxWeightConfirmed = value;
      }
    },

    vehicleRoadworthyClickHandler(
      value: boolean,
      event: HTMLInputElement,
    ): void {
      if (
        this.displayVehicleLookupButton &&
        !this.vehicleLookUp.vehicleRoadworthyValid &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        this.checkoutForm_checkFieldVisibility('plate_number')
      ) {
        if (event?.target instanceof HTMLInputElement) {
          event.target.checked = false;
        }
        this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
          'MOT validation failed';

        this.$openModal('GlobalModalError', {
          header:
            'Sorry, the MOT for this vehicle shows expiry before the car park exit date.',
          body: 'Please try a different vehicle or update your MOT before booking.',
          buttons: [
            {
              callback: this.changeVehicle,
              btnText: 'Change licence plate',
            },
            {
              callback: this.searchAgain,
              btnText: 'Search again',
            },
          ],
        })
          .then((callback) => {
            if (callback && typeof callback === 'function') {
              callback();
            }
          })
          .catch(() => {});
      } else {
        this.vehicleLookUp.vehicleRoadworthyConfirmed = value;
      }
    },

    handlePlateNumberInput() {
      this.vehicleLookUp.error = false;
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
    },
    setLoungePassAdditionalTravellersDetails() {
      if (this.displayLoungePassAdditionalTravellers) {
        const maxTickets: number = getAppVariable(
          'products.lounges.customer_groups.max_tickets',
        );
        const groups = this.loungePassItem.search_criteria.lounges.groups;
        const travellers = Object.entries(groups).flatMap(([type, count]) => {
          let adjustedCount = type === 'adult' ? Math.max(0, count - 1) : count;
          const maxAdditionalTravellers = maxTickets - 1;
          if (adjustedCount > maxAdditionalTravellers) {
            adjustedCount = maxAdditionalTravellers;
          }
          type = type === 'senior' ? 'adult' : type;
          return Array.from({ length: adjustedCount }, () => ({
            firstName: '',
            lastName: '',
            ageGroup: type,
            ageGroupLabel: this.loungePassCustomerGroupLabels[type],
          }));
        });

        this.loungePassAdditionalTravellersDetails = travellers;
      } else {
        this.loungePassAdditionalTravellersDetails = [];
      }
    },

    displaySycurioGateway(): boolean {
      return isSycurioFlow(this.$route);
    },
  },
});
